import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { storeUpdatedPayloadSchema } from '../data/receiving-ws';
import type { Store } from '../data/store';
import { storesQueryOptions } from '../query/query-options';
import { useStoreWebSocket } from '../websocket/use-store-websocket';

// A last_uploaded_at value reaches us either as a Date (the cached store
// record — Yup.date() validated by the fetch wrapper, and preserved as a Date
// through IndexedDB's structured clone) or an RFC3339 string (the WS payload,
// typed Yup.string()). Normalize to epoch millis; null/undefined/unparseable →
// null ("no known version").
function versionMillis(value: Date | string | null | undefined): number | null {
  if (value == null) {
    return null;
  }

  const ms = new Date(value).getTime();

  return Number.isNaN(ms) ? null : ms;
}

// storeId is passed in rather than read off storeLayout.useParams(): the store
// layout is no longer the only mount point — the onboarding page mounts this
// too, and it lives outside that route.
export function useStoreEventListener(storeId: string): void {
  const { subscribe } = useStoreWebSocket();
  const queryClient = useQueryClient();
  const lastVersionRef = useRef<number | null>(null);

  useEffect(() => {
    // Seed the baseline from the store record already in cache so the
    // reconciliation push the backend sends on every (re)connect does not force
    // a redundant refetch when our data is already current.
    const stores = queryClient.getQueryData<Store[]>(storesQueryOptions().queryKey);
    const cached = stores?.find((s) => s.id === storeId);
    lastVersionRef.current = versionMillis(cached?.last_uploaded_at);

    return subscribe('store_updated', storeUpdatedPayloadSchema, (payload) => {
      const incoming = versionMillis(payload.last_uploaded_at);

      // A version at or below what we already acted on is a stale/duplicate push
      // (e.g. the per-reconnect reconciliation when nothing changed) — ignore.
      // A missing version (older backend) can't be compared, so fall through and
      // invalidate unconditionally, preserving the original behavior.
      if (
        incoming !== null &&
        lastVersionRef.current !== null &&
        incoming <= lastVersionRef.current
      ) {
        return;
      }

      if (incoming !== null) {
        lastVersionRef.current = incoming;
      }

      queryClient.invalidateQueries({ queryKey: ['stores'], exact: true, refetchType: 'none' });
      queryClient.invalidateQueries({ queryKey: ['stores', storeId] });

      // The single-store record lives under its own key, not beneath the
      // ['stores', storeId] prefix above — that prefix covers the per-store
      // sub-resources (products, vendors, orders, …) and matches no query
      // holding the store itself. The onboarding page reads the store through
      // this key, so without it a push updates every sub-resource and leaves
      // the record that says "the first upload landed" stale. On the store
      // layout no such query is mounted and this is a no-op.
      queryClient.invalidateQueries({ queryKey: ['store', storeId] });
    });
  }, [subscribe, queryClient, storeId]);
}
